import { Fragment } from 'react';
import { Table, Loader } from '@mantine/core';
import { DotMenu, type DotMenuItem, RefetchButton } from '@shared/ui';
import './OptionsTable.css';

export interface OptionsTableColumn<T = any> {
    key: string;
    label: React.ReactNode;
    render?: (item: T) => React.ReactNode;
    style?: React.CSSProperties;
}

export interface OptionsTableProps<T = any> {
    columns: OptionsTableColumn<T>[];
    data: T[];
    loading?: boolean;
    error?: unknown;
    refetch?: () => void;
    getActions?: (item: T) => DotMenuItem[];
    emptyMessage?: string;
    onRowClick?: (item: T) => void;
    isRowExpanded?: (item: T) => boolean;
    renderExpandedRow?: (item: T) => React.ReactNode;
    /** Если задан — первая колонка объединяется по группам (rowSpan), значение берётся из первой строки группы */
    groupBy?: (item: T) => string | number;
    getRowClassName?: (item: T) => string | undefined;
}

function groupByKey<T>(data: T[], getKey: (item: T) => string | number): T[][] {
    const groups: T[][] = [];
    const keyToIndex = new Map<string | number, number>();
    for (const item of data) {
        const key = getKey(item);
        const idx = keyToIndex.get(key);
        if (idx === undefined) {
            keyToIndex.set(key, groups.length);
            groups.push([item]);
        } else {
            groups[idx].push(item);
        }
    }
    return groups;
}

export default function OptionsTable<T extends { id: number | string }>({
    columns,
    data,
    loading = false,
    error,
    refetch,
    getActions,
    emptyMessage = 'Нет данных',
    onRowClick,
    isRowExpanded,
    renderExpandedRow,
    groupBy,
    getRowClassName,
}: OptionsTableProps<T>) {
    if (error && refetch) {
        return (
            <RefetchButton
                refetch={refetch}
                isLoading={loading}
                errorMessage={error instanceof Error ? error.message : 'Не удалось загрузить данные'}
            />
        );
    }

    if (loading) {
        return (
            <div className="loaderContainer">
                <Loader />
            </div>
        );
    }

    const hasActions = !!getActions;
    const totalColumns = columns.length + (hasActions ? 1 : 0);
    const firstColumn = columns[0];
    const restColumns = columns.slice(1);

    const renderCell = (column: OptionsTableColumn<T>, item: T) =>
        column.render ? column.render(item) : (item as any)[column.key];

    return (
        <div className="options-table__scroll">
            <Table highlightOnHover={false} striped={false} withTableBorder={false} withColumnBorders={false} className="options-table">
                <Table.Thead>
                    <Table.Tr>
                        {columns.map((column) => (
                            <Table.Th key={column.key} style={column.style}>
                                {column.label}
                            </Table.Th>
                        ))}
                        {hasActions && (
                            <Table.Th className="options-table__actions-col" style={{ width: '44px' }}></Table.Th>
                        )}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.length > 0 ? (
                        groupBy ? (
                            groupByKey(data, groupBy).map((group) =>
                                group.map((item, i) => (
                                    <Fragment key={item.id}>
                                        <Table.Tr
                                            className={getRowClassName?.(item)}
                                            onClick={() => onRowClick?.(item)}
                                            style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                        >
                                            {i === 0 && (
                                                <Table.Td
                                                    key={firstColumn.key}
                                                    rowSpan={group.length}
                                                    style={{ verticalAlign: 'top', ...firstColumn.style }}
                                                >
                                                    {renderCell(firstColumn, item)}
                                                </Table.Td>
                                            )}
                                            {restColumns.map((column) => (
                                                <Table.Td key={column.key} style={column.style}>
                                                    {renderCell(column, item)}
                                                </Table.Td>
                                            ))}
                                            {hasActions && (
                                                <Table.Td className="options-table__actions-col" onClick={(e) => e.stopPropagation()}>
                                                    {getActions(item) && getActions(item).length > 0 && (
                                                        <DotMenu items={getActions(item)} />
                                                    )}
                                                </Table.Td>
                                            )}
                                        </Table.Tr>
                                        {isRowExpanded?.(item) && renderExpandedRow && (
                                            <Table.Tr>
                                                <Table.Td colSpan={totalColumns}>{renderExpandedRow(item)}</Table.Td>
                                            </Table.Tr>
                                        )}
                                    </Fragment>
                                ))
                            )
                        ) : (
                            data.map((item) => (
                                <Fragment key={item.id}>
                                    <Table.Tr
                                        className={getRowClassName?.(item)}
                                        onClick={() => onRowClick?.(item)}
                                        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {columns.map((column) => (
                                            <Table.Td key={column.key} style={column.style}>
                                                {renderCell(column, item)}
                                            </Table.Td>
                                        ))}
                                        {hasActions && (
                                            <Table.Td className="options-table__actions-col" onClick={(e) => e.stopPropagation()}>
                                                {getActions(item) && getActions(item).length > 0 && (
                                                    <DotMenu items={getActions(item)} />
                                                )}
                                            </Table.Td>
                                        )}
                                    </Table.Tr>
                                    {isRowExpanded?.(item) && renderExpandedRow && (
                                        <Table.Tr>
                                            <Table.Td colSpan={totalColumns}>{renderExpandedRow(item)}</Table.Td>
                                        </Table.Tr>
                                    )}
                                </Fragment>
                            ))
                        )
                    ) : (
                        <Table.Tr>
                            <Table.Td colSpan={totalColumns} className="options-table__empty">
                                {emptyMessage}
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </div>
    );
}
